import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Form, Button, Spinner, FloatingLabel } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { authHeaders, guidesFetch, loadGuideCategories } from './guidesUtils';

const emptyForm = { title: '', categoryId: '', topicId: '', description: '' };

export default function CreateGuideModal({ show, onHide, type, onCreated }) {
  const [form, setForm] = useState(emptyForm);
  const [coverFile, setCoverFile] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    setForm(emptyForm);
    setCoverFile(null);
    setLoadingCategories(true);
    loadGuideCategories()
      .then(setCategories)
      .catch((err) => toast.error(err.message || 'Failed to load categories'))
      .finally(() => setLoadingCategories(false));
  }, [show, type]);

  const topics = useMemo(() => {
    const cat = categories.find((c) => String(c.id) === String(form.categoryId));
    return cat?.topics || [];
  }, [categories, form.categoryId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!form.categoryId) {
      toast.error('Select a category');
      return;
    }
    setSaving(true);
    try {
      const selected = categories.find((c) => String(c.id) === String(form.categoryId));
      const path = type === 'article' ? '/articles' : '/notebooks';
      const created = await guidesFetch(path, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({
          title: form.title.trim(),
          categoryId: Number(form.categoryId),
          topicId: form.topicId ? Number(form.topicId) : null,
          category: selected?.name || null,
          description: form.description.trim() || null
        })
      });

      if (coverFile && created?.id) {
        const fd = new FormData();
        fd.append('cover', coverFile);
        const coverPath = type === 'article'
          ? `/articles/${created.id}/cover`
          : `/notebooks/${created.id}/cover`;
        const coverRes = await guidesFetch(coverPath, {
          method: 'POST',
          headers: authHeaders(),
          body: fd
        });
        created.coverImageUrl = coverRes.coverImageUrl;
      }

      toast.success(type === 'article' ? 'Article created' : 'Notebook created');
      onCreated?.(created);
      onHide();
    } catch (err) {
      toast.error(err.message || 'Failed to create');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton className="border-0">
          <div className="d-flex flex-column align-items-start w-100">
            <Modal.Title>{type === 'article' ? 'New Article' : 'New Notebook'}</Modal.Title>
            <div className="text-hl-success" />
          </div>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <FloatingLabel label="Title" controlId="title" className="required-field">
              <Form.Control
                value={form.title}
                onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                placeholder={type === 'article' ? 'Article title' : 'Notebook title'}
                required
                autoFocus
              />
            </FloatingLabel>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label className="required-field">Category</Form.Label>
            {loadingCategories ? (
              <div className="guides-loading guides-loading-inline"><Spinner size="sm" /></div>
            ) : (
              <Form.Select
                value={form.categoryId}
                onChange={(e) => setForm((f) => ({ ...f, categoryId: e.target.value, topicId: '' }))}
                required
              >
                <option value="">Select a specialty…</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </Form.Select>
            )}
          </Form.Group>
          {topics.length > 0 && (
            <Form.Group className="mb-3">
              <Form.Label>Topic <span className="text-muted fw-normal">(optional)</span></Form.Label>
              <Form.Select
                value={form.topicId}
                onChange={(e) => setForm((f) => ({ ...f, topicId: e.target.value }))}
              >
                <option value="">None</option>
                {topics.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          )}
          <Form.Group className="mb-3">
            <FloatingLabel label="Description (optional)" controlId="description">
              <Form.Control
                as="textarea"
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                placeholder="Short description"
                style={{ height: '100px' }}
              />
            </FloatingLabel>
          </Form.Group>
          <Form.Group>
            <Form.Control
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={(e) => setCoverFile(e.target.files?.[0] || null)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="outline-secondary" size="sm" onClick={onHide} disabled={saving}>Cancel</Button>
          <Button type="submit" variant="primary" size="sm" disabled={saving || loadingCategories}>
            {saving ? <Spinner size="sm" /> : 'Create'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
